import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Divider,
  Avatar
} from '@mui/material';
import {
  Edit as EditIcon,
  ArrowBack as ArrowBackIcon,
  Store as StoreIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Language as DomainIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import storeService from '../../services/storeService';

const StoreDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { t } = useTranslation();
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStore();
  }, [id]);

  const fetchStore = async () => {
    try {
      setLoading(true);
      const data = await storeService.getStoreById(id);
      setStore(data);
    } catch (error) {
      toast.error(t('common.loadError'));
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <Box sx={{ p: 3 }}>{t('common.loading')}</Box>;
  }

  if (!store) {
    return <Box sx={{ p: 3 }}>{t('common.notFound')}</Box>;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/stores')}
          >
            {t('common.back')}
          </Button>
          <Typography variant="h4">{t('stores.viewStore')}</Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<EditIcon />}
          onClick={() => navigate(`/stores/edit/${id}`)}
        >
          {t('stores.editStore')}
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* Main Information */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Avatar
                  src={store.logoUrl}
                  sx={{ width: 80, height: 80, mr: 2, bgcolor: 'primary.main' }}
                >
                  <StoreIcon sx={{ fontSize: 40 }} />
                </Avatar>
                <Box>
                  <Typography variant="h5">{store.name}</Typography>
                  <Chip label={store.code} color="primary" size="small" sx={{ mt: 1 }} />
                </Box>
              </Box>

              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={2}>
                {store.email && (
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <EmailIcon color="action" />
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          {t('common.email')}
                        </Typography>
                        <Typography variant="body1">{store.email}</Typography>
                      </Box>
                    </Box>
                  </Grid>
                )}

                {store.phone && (
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PhoneIcon color="action" />
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          {t('common.phone')}
                        </Typography>
                        <Typography variant="body1">{store.phone}</Typography>
                      </Box>
                    </Box>
                  </Grid>
                )}

                {store.address && (
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <LocationIcon color="action" />
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          {t('common.address')}
                        </Typography>
                        <Typography variant="body1">{store.address}</Typography>
                      </Box>
                    </Box>
                  </Grid>
                )}

                {store.domain && (
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <DomainIcon color="action" />
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          {t('common.domain')}
                        </Typography>
                        <Typography variant="body1">{store.domain}</Typography>
                      </Box>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Statistics */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('common.statistics')}
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    {t('common.status')}
                  </Typography>
                  <Typography variant="body1">
                    <Chip
                      label={store.isActive ? t('common.active') : t('common.inactive')}
                      color={store.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    {t('stores_ext.usersWithAccess')}
                  </Typography>
                  <Typography variant="h4" color="primary">
                    {store.userCount || 0}
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    {t('common.createdAt')}
                  </Typography>
                  <Typography variant="body2">
                    {new Date(store.createdAt).toLocaleDateString()}
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    {t('common.updatedAt')}
                  </Typography>
                  <Typography variant="body2">
                    {new Date(store.updatedAt).toLocaleDateString()}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Quick Actions */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('common.quickActions')}
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate(`/products?store=${store.id}`)}
                >
                  {t('stores_ext.viewProducts')}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => navigate(`/orders?store=${store.id}`)}
                >
                  {t('stores_ext.viewOrders')}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => navigate(`/stock-overview?store=${store.id}`)}
                >
                  {t('stores_ext.viewInventory')}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default StoreDetails;
